import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { post } from "@rails/request.js"
import controller from "../utils/controller"

export default class extends Controller {
  static targets = [
    "attachments",
    "attachmentTemplate",
    "attachableSelect",
    "smartLockModal",
    "smartLockModalBody",
  ]

  static values = {
    smartLockAccessGroupsUrl: String,
    permissionGroupId: Number,
  }

  connect() {
    if (this.attachments.length === 0) this.addAttachment()
    this.excludeAttached()
    this._submitting = false
    this._checkingSmartLock = false
  }

  addAttachment() {
    const id = new Date().getTime()
    const content = this.attachmentTemplateTarget.innerHTML.replace(/NEW_ATTACHMENT/g, id)
    this.attachmentsTarget.insertAdjacentHTML("beforeend", content)

    return this.element.querySelector(`#new_permission_group_attachment_${id}`)
  }

  excludeAttached() {
    const excludeIds = this.attachableSelectTargets
      .filter(field => this.attachments.some(container => container.contains(field)))
      .filter(field => field.value !== "")
      .map(field => field.value)

    this.attachableSelectTargets.forEach(field => {
      field.dataset.selectWithSearchExcludeValue = JSON.stringify(excludeIds)
    })
  }

  async handleSubmit(event) {
    event.preventDefault()

    if (this._submitting || this._checkingSmartLock) return

    const violatingAttachment = this.attachments
      .map(el => controller(el, "permission-group-form--attachment"))
      .find(c => c?.hasSmartLockScheduleViolation())

    if (violatingAttachment) {
      violatingAttachment.showSmartLockScheduleModal()
      violatingAttachment.smartLockScheduleModalTarget.addEventListener(
        "modal:hidden",
        () => this._reenableSubmitButton(),
        { once: true }
      )
      return
    }

    const gids = this.attachableSelectTargets
      .filter(field => this.attachments.some(container => container.contains(field)))
      .filter(field => field.value !== "")
      .map(field => field.value)

    if (gids.length === 0 || !this.hasSmartLockAccessGroupsUrlValue) {
      this._submit()
      return
    }

    this._checkingSmartLock = true
    this._disableSubmitButton()

    try {
      const response = await post(this.smartLockAccessGroupsUrlValue, {
        body: {
          gids,
          permission_group_id: this.permissionGroupIdValue || null,
        },
      })
      const data = await response.json

      if (data.message) {
        this._reenableSubmitButton()
        this.showSmartLockConflictModal(data.message)
      } else {
        this._submit()
      }
    } catch {
      this._submit()
    } finally {
      this._checkingSmartLock = false
    }
  }

  _submit() {
    if (this._submitting) return
    this._submitting = true
    // Submit via Turbo's navigator instead of requestSubmit() to avoid dispatching
    // a `submit` event — multiple listeners on the form would otherwise each fire a
    // request, causing concurrent PATCHes that deadlock at the DB.
    Turbo.navigator.submitForm(this.element)
  }

  showSmartLockConflictModal(message) {
    const p = document.createElement("p")
    p.textContent = message
    this.smartLockModalBodyTarget.replaceChildren(p)
    this.smartLockModal.open()
  }

  cancelSmartLockModal() {
    this.smartLockModal.close()
    this._reenableSubmitButton()
  }

  continueSmartLockModal() {
    this.smartLockModal.close()
    this._submit()
  }

  get smartLockModal() {
    return controller(this.smartLockModalTarget, "modal")
  }

  _disableSubmitButton() {
    const submitButton = this.element.querySelector("[type='submit']")
    if (submitButton) submitButton.disabled = true
  }

  _reenableSubmitButton() {
    const submitButton = this.element.querySelector("[type='submit']")
    if (!submitButton) return
    setTimeout(() => { submitButton.disabled = false }, 0)
  }

  // Returns attachments that haven't been removed.
  //
  // TODO: Use Stimulus outlets if custom outlet names ever get implemented:
  // https://github.com/hotwired/stimulus/issues/717
  get attachments() {
    return Array.from(this.attachmentsTarget.children).filter(attachment => {
      const style = window.getComputedStyle(attachment)
      return style.display !== "none"
    })
  }
}
